import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  Send,
  Save,
  Loader2,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { formatDistanceToNow, isPast, parseISO, differenceInDays, format } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { usePortalAssessment, useSaveResponses, useSubmitAssessment, useBypassAttest, usePortalCertifications } from '@/api/hooks/usePortal';
import { cn } from '@/lib/utils';
import type { PortalSection, PortalQuestion, PortalCertification } from '@/api/types/assessment.types';

// ── Question input components ─────────────────────────────────────────────────

function YesNoInput({
  value, onChange, readOnly,
}: { value: boolean | null; onChange: (v: boolean) => void; readOnly?: boolean }) {
  return (
    <div className="flex gap-2">
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange(v)}
          className={cn(
            'w-24 rounded-lg border py-2 text-sm font-medium transition-colors',
            readOnly && 'cursor-default opacity-80',
            value === v
              ? v
                ? 'border-accent-emerald bg-accent-emerald/15 text-accent-emerald'
                : 'border-accent-rose bg-accent-rose/15 text-accent-rose'
              : 'border-border-subtle bg-bg-surface text-text-secondary hover:bg-bg-surface-2',
          )}
        >
          {v ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );
}

function MultiChoiceInput({
  options, selected, onChange, readOnly,
}: { options: string[]; selected: string[]; onChange: (v: string[]) => void; readOnly?: boolean }) {
  function toggle(opt: string) {
    if (readOnly) return;
    const s = new Set(selected);
    if (s.has(opt)) s.delete(opt); else s.add(opt);
    onChange([...s]);
  }
  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          disabled={readOnly}
          onClick={() => toggle(opt)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-sm text-left transition-colors',
            readOnly ? 'cursor-default' : 'hover:bg-bg-surface-2',
            selected.includes(opt)
              ? 'border-accent-cyan bg-accent-cyan/10 text-text-primary'
              : 'border-border-subtle bg-bg-surface text-text-secondary',
          )}
        >
          <div className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
            selected.includes(opt) ? 'border-accent-cyan bg-accent-cyan' : 'border-border-subtle',
          )}>
            {selected.includes(opt) && <CheckCircle2 className="h-3 w-3 text-bg-base" />}
          </div>
          {opt}
        </button>
      ))}
    </div>
  );
}

// ── Per-question input connected to auto-save ──────────────────────────────────

function QuestionInput({
  question,
  onSave,
  readOnly,
  isBypassApplied,
}: {
  question: PortalQuestion;
  onSave: (questionId: string, responseValue: string, vendorClarification?: string) => void;
  readOnly?: boolean;
  isBypassApplied?: boolean;
}) {
  const [textValue, setTextValue] = useState(question.response_text ?? '');
  const [boolValue, setBoolValue] = useState<boolean | null>(question.response_boolean ?? null);
  const [optionsValue, setOptionsValue] = useState<string[]>(question.response_options ?? []);
  const [clarification, setClarification] = useState(question.vendor_clarification ?? '');

  // Sync boolean/options from fresh query data when a background refetch lands
  // after invalidation. Covers the narrow race where the component remounts from
  // stale cache before the refetch completes — props update and we catch it here.
  // Text/clarification deliberately excluded: 2s debounced saves create a clobber
  // risk if a refetch lands while the user is mid-typing.
  useEffect(() => { setBoolValue(question.response_boolean ?? null); }, [question.response_boolean]);
  useEffect(() => { setOptionsValue(question.response_options ?? []); }, [question.response_options]);

  // Debounce text + clarification inputs
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clarDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleTextSave(value: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSave(question.id, value, clarification), 2000);
  }

  function handleTextChange(value: string) {
    setTextValue(value);
    scheduleTextSave(value);
  }

  function handleClarificationChange(value: string) {
    setClarification(value);
    if (clarDebounceRef.current) clearTimeout(clarDebounceRef.current);
    // Determine current response value to pass alongside clarification
    let currentResponse = textValue;
    if (question.question_type === 'yes_no') currentResponse = boolValue === null ? '' : boolValue ? 'Yes' : 'No';
    if (question.question_type === 'multiple_choice') currentResponse = JSON.stringify(optionsValue);
    clarDebounceRef.current = setTimeout(() => onSave(question.id, currentResponse, value), 2000);
  }

  function handleBoolChange(v: boolean) {
    setBoolValue(v);
    onSave(question.id, v ? 'Yes' : 'No', clarification);
  }

  function handleOptionsChange(v: string[]) {
    setOptionsValue(v);
    onSave(question.id, JSON.stringify(v), clarification);
  }

  const isAnswered =
    question.question_type === 'yes_no' ? boolValue !== null :
    question.question_type === 'multiple_choice' ? optionsValue.length > 0 :
    textValue.trim().length > 0;

  if (question.is_auto_scored === true && isBypassApplied === true) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-text-secondary italic">Credited via certification bypass</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {question.guidance_text && (
        <p className="text-xs text-text-secondary italic">{question.guidance_text}</p>
      )}

      {question.is_sent_back && question.analyst_note && (
        <div className="rounded-md border border-accent-amber/40 bg-accent-amber/10 px-3 py-2.5">
          <p className="text-xs font-medium text-accent-amber">Reviewer requested clarification:</p>
          <p className="text-sm text-text-primary mt-1">{question.analyst_note}</p>
        </div>
      )}

      {question.question_type === 'yes_no' && (
        readOnly
          ? <p className="text-sm font-medium text-text-primary">
              {boolValue === null ? '—' : boolValue ? 'Yes' : 'No'}
            </p>
          : <YesNoInput value={boolValue} onChange={handleBoolChange} />
      )}

      {question.question_type === 'multiple_choice' && (
        readOnly
          ? <div className="space-y-1">
              {optionsValue.length > 0
                ? optionsValue.map((o) => <p key={o} className="text-sm text-text-primary">• {o}</p>)
                : <p className="text-sm text-text-secondary">—</p>}
            </div>
          : <MultiChoiceInput
              options={question.options ?? []}
              selected={optionsValue}
              onChange={handleOptionsChange}
            />
      )}

      {(question.question_type === 'free_text' || question.question_type === 'number' || question.question_type === 'date') && (
        readOnly
          ? <p className="text-sm text-text-primary whitespace-pre-wrap">{textValue || '—'}</p>
          : question.question_type === 'number'
            ? <input
                type="number"
                value={textValue}
                onChange={(e) => handleTextChange(e.target.value)}
                className="w-full rounded-lg border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
              />
            : question.question_type === 'date'
              ? <input
                  type="date"
                  value={textValue}
                  onChange={(e) => handleTextChange(e.target.value)}
                  className="w-full rounded-lg border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
                />
              : <textarea
                  value={textValue}
                  onChange={(e) => handleTextChange(e.target.value)}
                  placeholder="Enter your response…"
                  rows={4}
                  className="w-full resize-y rounded-lg border border-border-subtle bg-bg-surface-2 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
                />
      )}

      {question.question_type === 'scale_1_5' && (
        readOnly
          ? <p className="text-sm text-text-primary">{textValue || '—'} / 5</p>
          : <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleTextChange(String(n))}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg border text-sm font-medium transition-colors',
                    textValue === String(n)
                      ? 'border-accent-cyan bg-accent-cyan/15 text-accent-cyan'
                      : 'border-border-subtle bg-bg-surface text-text-secondary hover:bg-bg-surface-2',
                  )}
                >
                  {n}
                </button>
              ))}
              <span className="self-center text-xs text-text-secondary ml-1">1=Poor · 5=Excellent</span>
            </div>
      )}

      {question.question_type === 'file_upload' && (
        <div className={cn(
          'rounded-lg border-2 border-dashed p-8 text-center',
          readOnly ? 'border-border-subtle bg-bg-surface-2' : 'border-border-subtle bg-bg-surface',
        )}>
          {question.file_url
            ? <p className="text-sm text-accent-cyan">File uploaded: {question.file_url.split('/').pop()}</p>
            : readOnly
              ? <p className="text-sm text-text-secondary">No file uploaded</p>
              : <p className="text-sm text-text-secondary">
                  Drag &amp; drop a file, or use the evidence upload endpoint
                </p>
          }
        </div>
      )}

      {/* Vendor clarification — editable when active, read-only when approved */}
      {readOnly ? (
        clarification && (
          <div className="rounded border-l-2 border-accent-cyan/40 pl-3">
            <p className="text-xs text-text-secondary">Your comment:</p>
            <p className="text-sm text-text-primary whitespace-pre-wrap">{clarification}</p>
          </div>
        )
      ) : (
        <div className="space-y-1">
          <textarea
            value={clarification}
            onChange={(e) => handleClarificationChange(e.target.value)}
            placeholder="Add a comment (optional)…"
            rows={2}
            maxLength={1000}
            className="w-full resize-none rounded-lg border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-1 focus:ring-accent-cyan/50"
          />
        </div>
      )}

      {!readOnly && (
        <p className={cn(
          'text-xs transition-colors',
          isAnswered ? 'text-accent-emerald' : 'text-text-secondary/40',
        )}>
          {isAnswered ? '✓ Answered' : question.is_required ? 'Required' : 'Optional'}
        </p>
      )}
    </div>
  );
}

// ── Bypass attestation modal ──────────────────────────────────────────────────

function BypassAttestModal({
  open,
  certs,
  onConfirm,
  onCancel,
  isPending,
}: {
  open: boolean;
  certs: PortalCertification[];
  onConfirm: (certIds: string[]) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (!open) return null;

  const validCerts = certs.filter(
    (c) => c.lifecycle_status === 'active' && (!c.expiry_date || new Date(c.expiry_date) > new Date()),
  );

  function toggleCert(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-5 shadow-2xl space-y-4 max-h-[80vh] flex flex-col">
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-text-primary">Attest with Certifications</h3>
          <p className="text-sm text-text-secondary">
            Select the certifications that apply to this assessment. Covered questions will be marked as answered via your certification.
          </p>
        </div>

        {validCerts.length === 0 ? (
          <p className="text-sm text-text-secondary italic py-4 text-center">
            No verified, non-expired certifications found in your library.
          </p>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2">
            {validCerts.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCert(c.id)}
                className={cn(
                  'w-full flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                  selected.has(c.id)
                    ? 'border-accent-emerald bg-accent-emerald/10'
                    : 'border-border-subtle bg-bg-surface hover:bg-bg-surface-2',
                )}
              >
                <div className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                  selected.has(c.id) ? 'border-accent-emerald bg-accent-emerald' : 'border-border-subtle',
                )}>
                  {selected.has(c.id) && <CheckCircle2 className="h-3 w-3 text-bg-base" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary">{c.cert_type.replace(/_/g, ' ')}</p>
                  {c.issuing_body && <p className="text-xs text-text-secondary truncate">{c.issuing_body}</p>}
                  {c.expiry_date && (
                    <p className="text-xs text-text-secondary">
                      Expires {new Date(c.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-border-subtle">
          <Button variant="ghost" className="flex-1" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => onConfirm([...selected])}
            disabled={isPending || selected.size === 0}
          >
            {isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Attesting…</> : `Attest (${selected.size} selected)`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Submit confirmation modal ─────────────────────────────────────────────────

function SubmitModal({
  open, tenantName, onConfirm, onCancel, isPending,
}: {
  open: boolean;
  tenantName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-xl border border-border-subtle bg-bg-surface p-5 shadow-2xl space-y-4">
        <h3 className="text-base font-semibold text-text-primary">Submit Assessment</h3>
        <p className="text-sm text-text-secondary">
          Submit your assessment to{' '}
          <span className="font-medium text-text-primary">{tenantName}</span>?
          Once submitted, you will not be able to edit your responses.
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={onConfirm} disabled={isPending}>
            {isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Save status indicator ─────────────────────────────────────────────────────

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// ── Main page ─────────────────────────────────────────────────────────────────

export function PortalAssessmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: assessment, isLoading } = usePortalAssessment(id);
  const saveMutation = useSaveResponses(id!);
  const submitMutation = useSubmitAssessment(id!);
  const bypassAttestMutation = useBypassAttest(id!);
  const { data: certs = [] } = usePortalCertifications();

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [bypassOpen, setBypassOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveStatusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeSection = assessment?.sections.find((s) => s.id === activeSectionId)
    ?? assessment?.sections[0];

  const isReadOnly = assessment?.status === 'approved';
  const isSentBack = assessment
    ? assessment.sections.some((s: PortalSection) =>
        s.questions.some((q: PortalQuestion) => q.is_sent_back),
      )
    : false;

  const bypassCert = assessment?.certification_bypass_applied
    ? certs.find((c) => (assessment.bypassed_cert_ids ?? []).includes(c.id))
    : undefined;

  // Compute unanswered required questions count across all sections
  const unansweredCount = (assessment?.sections ?? []).reduce((total: number, s: PortalSection) =>
    total + s.questions.filter((q: PortalQuestion) => {
      if (!q.is_required) return false;
      return q.response_text === null && q.response_boolean === null &&
             (!q.response_options || q.response_options.length === 0) && q.file_url === null;
    }).length, 0,
  );

  function isSectionComplete(section: PortalSection): boolean {
    return section.questions.every((q: PortalQuestion) => {
      if (!q.is_required) return true;
      return q.response_text !== null || q.response_boolean !== null ||
             (q.response_options !== null && q.response_options.length > 0) || q.file_url !== null;
    });
  }

  function handleSave(responses: Array<{ questionId: string; responseValue: string; vendorClarification?: string }>) {
    if (isReadOnly || responses.length === 0) return;
    setSaveStatus('saving');
    saveMutation.mutate(responses, {
      onSuccess: () => {
        setSaveStatus('saved');
        if (saveStatusTimeout.current) clearTimeout(saveStatusTimeout.current);
        saveStatusTimeout.current = setTimeout(() => setSaveStatus('idle'), 2500);
      },
      onError: () => setSaveStatus('error'),
    });
  }

  function handleSaveDraft() {
    toast.promise(
      saveMutation.mutateAsync([
        // No-op save with an empty array to trigger completionPct refresh
        // The individual question saves are debounced; this forces a flush
      ]).catch(() => {}),
      { loading: 'Saving draft…', success: 'Draft saved', error: 'Save failed' },
    );
  }

  function handleSubmit() {
    submitMutation.mutate(undefined, {
      onSuccess: () => {
        setSubmitOpen(false);
        navigate(`/portal/assessments/${id}/submitted`);
      },
      onError: (err) => {
        setSubmitOpen(false);
        toast.error((err as Error).message || 'Submit failed');
      },
    });
  }

  function handleBypassAttest(certIds: string[]) {
    const allQuestionIds = (assessment?.sections ?? []).flatMap((s: PortalSection) => s.questions.map((q: PortalQuestion) => q.id));
    bypassAttestMutation.mutate(
      { question_ids: allQuestionIds, certification_ids: certIds },
      {
        onSuccess: (result) => {
          setBypassOpen(false);
          toast.success(`${result.questionsApplied} question${result.questionsApplied !== 1 ? 's' : ''} covered by your certifications.`);
        },
        onError: (err) => {
          setBypassOpen(false);
          toast.error((err as Error).message || 'Attestation failed');
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-96" />
          <div className="col-span-3 space-y-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <AlertCircle className="h-10 w-10 text-accent-rose/60" />
        <p className="text-text-secondary">Assessment not found</p>
        <Button variant="outline" onClick={() => navigate('/portal/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const overdue = assessment.due_date ? isPast(parseISO(assessment.due_date)) : false;
  const daysLeft = assessment.due_date ? differenceInDays(parseISO(assessment.due_date), new Date()) : null;
  const progressPct = assessment.total_questions > 0
    ? ((assessment.total_questions - unansweredCount) / assessment.total_questions) * 100
    : 0;

  return (
    <div className="space-y-4">
      {/* Back */}
      <button
        onClick={() => navigate('/portal/dashboard')}
        className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        Dashboard
      </button>

      {/* Approved banner */}
      {isReadOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-accent-emerald/30 bg-accent-emerald/10 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-accent-emerald" />
          <p className="text-sm text-accent-emerald font-medium">
            This assessment was approved
            {assessment.approved_at
              ? ` on ${new Date(assessment.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
              : ''}.
          </p>
        </div>
      )}

      {/* Sent-back banner */}
      {isSentBack && !isReadOnly && (
        <div className="flex items-start gap-2 rounded-lg border border-accent-amber/30 bg-accent-amber/10 px-4 py-3">
          <AlertCircle className="h-4 w-4 shrink-0 text-accent-amber mt-0.5" />
          <div>
            <p className="text-sm font-medium text-accent-amber">
              This assessment has been sent back for revision.
            </p>
            {assessment.notes_to_vendor && (
              <p className="text-sm text-text-primary mt-1">{assessment.notes_to_vendor}</p>
            )}
            <p className="text-xs text-text-secondary mt-1">
              Please review the questions marked below and resubmit.
            </p>
          </div>
        </div>
      )}

      {/* Bypass attestation banner — shown when template allows bypass and assessment is active */}
      {assessment.allow_bypass && !isReadOnly && (assessment.status === 'issued' || assessment.status === 'in_progress') && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-accent-cyan/30 bg-accent-cyan/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0 text-accent-cyan" />
            <div>
              <p className="text-sm font-medium text-accent-cyan">Certification bypass available</p>
              <p className="text-xs text-text-secondary mt-0.5">
                Your certifications may cover some questions. Attest now to skip covered questions.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-accent-cyan/40 text-accent-cyan hover:bg-accent-cyan/10"
            onClick={() => setBypassOpen(true)}
            disabled={bypassAttestMutation.isPending}
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Attest
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-border-subtle bg-bg-surface p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
          <div className="space-y-1">
            <p className="text-xs text-text-secondary">{assessment.tenant_name}</p>
            <h1 className="text-lg font-bold text-text-primary">{assessment.title}</h1>
            <StatusBadge status={assessment.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {assessment.due_date && !isReadOnly && (
              <div className={cn(
                'flex items-center gap-1.5 text-sm',
                overdue ? 'text-accent-rose' : daysLeft != null && daysLeft < 7 ? 'text-accent-amber' : 'text-text-secondary',
              )}>
                {overdue ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                <span>
                  {overdue
                    ? `Overdue ${formatDistanceToNow(parseISO(assessment.due_date), { addSuffix: true })}`
                    : daysLeft != null ? `${daysLeft}d remaining` : ''}
                </span>
              </div>
            )}

            {/* Save status */}
            {!isReadOnly && (
              <span className={cn('text-xs', saveStatus === 'saving' && 'text-text-secondary', saveStatus === 'saved' && 'text-accent-emerald', saveStatus === 'error' && 'text-accent-rose')}>
                {saveStatus === 'saving' && <><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Saving…</>}
                {saveStatus === 'saved' && '✓ Saved'}
                {saveStatus === 'error' && '⚠ Save failed'}
              </span>
            )}

            {isReadOnly ? (
              <Lock className="h-4 w-4 text-text-secondary" />
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveDraft}
                  disabled={saveMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-1.5" />
                  Save Draft
                </Button>
                <Button
                  size="sm"
                  onClick={() => setSubmitOpen(true)}
                  disabled={unansweredCount > 0}
                  title={unansweredCount > 0 ? `${unansweredCount} required questions unanswered` : undefined}
                >
                  <Send className="h-4 w-4 mr-1.5" />
                  Submit
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Progress */}
        {!isReadOnly && (
          <div className="space-y-2">
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-text-secondary">
                <span>
                  {assessment.total_questions - unansweredCount} / {assessment.total_questions} questions answered
                  {unansweredCount > 0 && <span className="text-accent-rose ml-1">· {unansweredCount} required remaining</span>}
                </span>
                <span className="font-mono">{Math.round(progressPct)}%</span>
              </div>
              <div className="h-2 rounded-full bg-bg-surface-2">
                <div
                  className={cn('h-full rounded-full transition-all', progressPct === 100 ? 'bg-accent-emerald' : 'bg-accent-cyan')}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-text-secondary">
              Autosave is enabled. To make sure your latest changes are captured, please click{' '}
              <span className="font-medium">Save Draft</span> or{' '}
              <span className="font-medium">Submit</span> before leaving this page.
            </p>
          </div>
        )}
      </div>

      {/* Body: sidebar + questions */}
      <div className="flex gap-4">
        {/* Section nav — desktop sidebar */}
        <div className="hidden lg:block w-56 shrink-0 space-y-1">
          {assessment.sections.map((section: PortalSection) => {
            const complete = isSectionComplete(section);
            const active = (activeSectionId ?? assessment.sections[0]?.id) === section.id;
            const hasSentBack = section.questions.some((q: PortalQuestion) => q.is_sent_back);
            return (
              <button
                key={section.id}
                onClick={() => setActiveSectionId(section.id)}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  active ? 'bg-bg-surface-2 text-text-primary font-medium' : 'text-text-secondary hover:bg-bg-surface-2',
                )}
              >
                <span className="line-clamp-1 flex-1">{section.name}</span>
                {hasSentBack && !isReadOnly && (
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 text-accent-amber ml-1" />
                )}
                {complete
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-accent-emerald ml-1" />
                  : <ChevronRight className="h-4 w-4 shrink-0 text-text-secondary ml-1" />
                }
              </button>
            );
          })}
        </div>

        {/* Questions panel */}
        <div className="flex-1 space-y-4 min-w-0">
          {/* Mobile section selector */}
          <div className="lg:hidden">
            <select
              value={activeSectionId ?? assessment.sections[0]?.id ?? ''}
              onChange={(e) => setActiveSectionId(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
            >
              {assessment.sections.map((s: PortalSection) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {activeSection && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-text-primary">{activeSection.name}</h2>
                <Badge variant="muted" className="font-mono text-[10px] shrink-0">
                  {activeSection.questions.length} questions
                </Badge>
              </div>

              {activeSection.description && (
                <p className="text-sm text-text-secondary">{activeSection.description}</p>
              )}

              {activeSection.questions.map((q: PortalQuestion, idx: number) => (
                <Card key={q.id}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bg-surface-2 text-xs font-mono text-text-secondary">
                        {idx + 1}
                      </span>
                      <div className="flex-1 space-y-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary">{q.question_text}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {q.is_required && <Badge variant="rose" className="text-[10px] py-0">Required</Badge>}
                          {q.is_sent_back && !isReadOnly && <Badge variant="amber" className="text-[10px] py-0">Needs clarification</Badge>}
                          {q.is_auto_scored === true && assessment.certification_bypass_applied === true && (
                            <StatusBadge
                              status="bypassed"
                              label={bypassCert
                                ? `Bypassed via ${bypassCert.cert_type} (valid through ${bypassCert.expiry_date ? format(parseISO(bypassCert.expiry_date), 'MMM d, yyyy') : 'N/A'})`
                                : 'Bypassed by certification'
                              }
                              className="text-[10px] py-0"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <QuestionInput
                      question={q}
                      readOnly={isReadOnly}
                      isBypassApplied={assessment.certification_bypass_applied === true}
                      onSave={(questionId, responseValue, vendorClarification) =>
                        handleSave([{ questionId, responseValue, vendorClarification }])
                      }
                    />
                  </CardContent>
                </Card>
              ))}

              {/* Section navigation */}
              <div className="flex justify-between pt-2">
                {assessment.sections.indexOf(activeSection) > 0 && (
                  <Button variant="outline" size="sm" onClick={() => {
                    const idx = assessment.sections.indexOf(activeSection);
                    setActiveSectionId(assessment.sections[idx - 1].id);
                  }}>
                    Previous Section
                  </Button>
                )}
                {assessment.sections.indexOf(activeSection) < assessment.sections.length - 1 && (
                  <Button size="sm" className="ml-auto" onClick={() => {
                    const idx = assessment.sections.indexOf(activeSection);
                    setActiveSectionId(assessment.sections[idx + 1].id);
                  }}>
                    Next Section
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <SubmitModal
        open={submitOpen}
        tenantName={assessment.tenant_name}
        onConfirm={handleSubmit}
        onCancel={() => setSubmitOpen(false)}
        isPending={submitMutation.isPending}
      />

      <BypassAttestModal
        open={bypassOpen}
        certs={certs}
        onConfirm={handleBypassAttest}
        onCancel={() => setBypassOpen(false)}
        isPending={bypassAttestMutation.isPending}
      />
    </div>
  );
}
